import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { message } from 'antd';

const useIdtCollaboration = (encounterId, onFieldUpdate) => {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({}); // fieldName -> array of users
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  
  // Refs to prevent stale closures
  const onFieldUpdateRef = useRef(onFieldUpdate);
  const userRef = useRef(user);
  
  useEffect(() => {
    onFieldUpdateRef.current = onFieldUpdate;
    userRef.current = user;
  });

  // Initialize socket connection
  useEffect(() => {
    if (!token || !user || !encounterId) {
      console.log('🔗 IDT collaboration prerequisites not met:', {
        hasToken: !!token,
        hasUser: !!user,
        hasEncounterId: !!encounterId
      });
      return;
    }

    console.log('🔗 Initializing IDT collaboration socket for encounter:', encounterId);

    // Determine the correct Socket.IO URL
    // Remove /api/v1 from the API URL for Socket.IO connection
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const socketUrl = apiUrl.replace('/api/v1', ''); // Remove API path for Socket.IO

    console.log('🔗 API URL (REST):', apiUrl);
    console.log('🔗 Socket.IO URL (corrected):', socketUrl);
    console.log('🔗 Using token:', token ? 'Token present' : 'No token');
    console.log('🔗 Environment:', process.env.NODE_ENV);

    const socketInstance = io(socketUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      timeout: 30000, // Increased timeout for production
      forceNew: true,
      autoConnect: true,
      // Production-specific settings
      upgrade: true,
      rememberUpgrade: true,
      // Handle connection retries
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      // Force polling first, then upgrade to websocket
      forceBase64: false
    });

    // Connection event handlers
    socketInstance.on('connect', () => {
      console.log('✅ IDT collaboration socket connected');
      setIsConnected(true);
      setConnectionError(null);
      
      // Join the IDT encounter room
      socketInstance.emit('join-idt-encounter', encounterId);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('🔌 IDT collaboration socket disconnected:', reason);
      setIsConnected(false);
      setActiveUsers([]);
      setTypingUsers({});

      // Handle authentication-related disconnections
      if (reason === 'io server disconnect') {
        console.log('🔌 Server disconnected the socket (likely authentication failure)');
        setConnectionError('Authentication failed - please refresh the page');
      }
    });

    socketInstance.on('connect_error', (error) => {
      console.error('❌ IDT collaboration socket connection error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        description: error.description,
        context: error.context,
        type: error.type,
        stack: error.stack
      });
      console.error('❌ Socket transport:', socketInstance.io.engine.transport?.name);
      console.error('❌ Socket URL:', socketInstance.io.uri);
      console.error('❌ Expected URL:', socketUrl);

      // Handle specific production errors
      let errorMessage = error.message;
      if (error.message.includes('Invalid namespace')) {
        errorMessage = 'Authentication failed - please refresh the page and try again';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Connection timeout - please check your internet connection';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'Connection blocked - please contact support';
      }

      setConnectionError(errorMessage);
      setIsConnected(false);
    });

    // IDT collaboration event handlers
    socketInstance.on('idt-active-users', (users) => {
      console.log('👥 Active IDT users:', users);
      setActiveUsers(users.filter(u => u.userId !== user._id));
    });

    socketInstance.on('idt-user-joined', (userInfo) => {
      console.log('👋 User joined IDT:', userInfo);
      if (userInfo.userId !== user._id) {
        setActiveUsers(prev => [...prev.filter(u => u.userId !== userInfo.userId), userInfo]);
        message.info(`${userInfo.name} joined the IDT Note`, 2);
      }
    });

    socketInstance.on('idt-user-left', (userInfo) => {
      console.log('👋 User left IDT:', userInfo);
      setActiveUsers(prev => prev.filter(u => u.userId !== userInfo.userId));
      setTypingUsers(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(fieldName => {
          updated[fieldName] = updated[fieldName].filter(u => u.userId !== userInfo.userId);
        });
        return updated;
      });
      message.info(`${userInfo.name} left the IDT Note`, 2);
    });

    socketInstance.on('idt-field-updated', (data) => {
      console.log('📝 IDT field updated:', data);
      const { fieldName, value, updatedBy, timestamp } = data;
      
      // Call the callback to update the form
      if (onFieldUpdateRef.current) {
        onFieldUpdateRef.current(fieldName, value, updatedBy, timestamp);
      }
      
      // Show notification
      message.success(`${updatedBy.name} updated ${fieldName}`, 2);
    });

    socketInstance.on('idt-user-typing', (data) => {
      const { fieldName, user: typingUser } = data;
      console.log('⌨️ User typing:', typingUser.name, 'in field:', fieldName);
      
      setTypingUsers(prev => ({
        ...prev,
        [fieldName]: [...(prev[fieldName] || []).filter(u => u.userId !== typingUser.userId), typingUser]
      }));
    });

    socketInstance.on('idt-user-stopped-typing', (data) => {
      const { fieldName, userId } = data;
      console.log('⏹️ User stopped typing:', userId, 'in field:', fieldName);
      
      setTypingUsers(prev => ({
        ...prev,
        [fieldName]: (prev[fieldName] || []).filter(u => u.userId !== userId)
      }));
    });

    socketInstance.on('idt-update-error', (data) => {
      console.error('❌ IDT update error:', data);
      message.error(`Failed to update ${data.fieldName}: ${data.error}`, 3);
    });

    // Handle version conflicts
    socketInstance.on('idt-version-conflict', (data) => {
      console.warn('⚠️ Version conflict detected:', data);
      const { fieldName, serverVersion, clientVersion, serverValue, clientValue } = data;
      message.warning(`Field "${fieldName}" was modified by another user. Your changes may conflict.`, 5);
    });

    // Handle field update confirmations
    socketInstance.on('idt-field-update-confirmed', (data) => {
      console.log('✅ Field update confirmed:', data);
      // Update local version tracking if needed
    });

    // Handle field locking
    socketInstance.on('idt-field-locked', (data) => {
      console.log('🔒 Field locked:', data);
      const { fieldName, lockedBy } = data;
      message.info(`${lockedBy.name} is actively editing "${fieldName}"`, 3);
    });

    socketInstance.on('idt-field-unlocked', (data) => {
      console.log('🔓 Field unlocked:', data);
    });

    socketInstance.on('idt-field-lock-granted', (data) => {
      console.log('✅ Field lock granted:', data);
    });

    socketInstance.on('idt-field-lock-denied', (data) => {
      console.log('❌ Field lock denied:', data);
      const { fieldName, lockedBy } = data;
      message.warning(`Cannot edit "${fieldName}" - currently being edited by ${lockedBy.name}`, 4);
    });

    // Handle authentication errors from server
    socketInstance.on('error', (error) => {
      console.error('❌ Socket.IO server error:', error);
      if (error.message && error.message.includes('Authentication')) {
        setConnectionError('Authentication failed - please refresh the page');
        message.error('Real-time collaboration authentication failed', 3);
      }
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up IDT collaboration socket');
      if (socketInstance) {
        socketInstance.emit('leave-idt-encounter', encounterId);
        socketInstance.disconnect();
      }
    };
  }, [token, user, encounterId]);

  // Send field update to other users
  const sendFieldUpdate = useCallback((fieldName, value) => {
    if (socket && isConnected) {
      console.log('📤 Sending IDT field update:', fieldName);
      socket.emit('idt-field-update', {
        encounterId,
        fieldName,
        value,
        timestamp: new Date().toISOString()
      });
    }
  }, [socket, isConnected, encounterId]);

  // Send typing indicator
  const sendTypingIndicator = useCallback((fieldName, isTyping) => {
    if (socket && isConnected) {
      if (isTyping) {
        socket.emit('idt-field-focus', { encounterId, fieldName });
      } else {
        socket.emit('idt-field-blur', { encounterId, fieldName });
      }
    }
  }, [socket, isConnected, encounterId]);

  // Get typing users for a specific field
  const getTypingUsers = useCallback((fieldName) => {
    return typingUsers[fieldName] || [];
  }, [typingUsers]);

  // Get user color for visual indicators
  const getUserColor = useCallback((userId) => {
    const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];
    const index = activeUsers.findIndex(u => u.userId === userId);
    return colors[index % colors.length] || '#1890ff';
  }, [activeUsers]);

  // Request field lock
  const requestFieldLock = useCallback((fieldName) => {
    if (socket && isConnected) {
      console.log('🔒 Requesting field lock:', fieldName);
      socket.emit('idt-field-lock-request', { encounterId, fieldName });
    }
  }, [socket, isConnected, encounterId]);

  // Release field lock
  const releaseFieldLock = useCallback((fieldName) => {
    if (socket && isConnected) {
      console.log('🔓 Releasing field lock:', fieldName);
      socket.emit('idt-field-unlock', { encounterId, fieldName });
    }
  }, [socket, isConnected, encounterId]);

  return {
    isConnected,
    connectionError,
    activeUsers,
    typingUsers,
    sendFieldUpdate,
    sendTypingIndicator,
    getTypingUsers,
    getUserColor,
    requestFieldLock,
    releaseFieldLock
  };
};

export default useIdtCollaboration;
